/**
 * CobrandCarousel Organism Component
 *
 * A high-level carousel orchestrator that integrates with AEM authoring.
 * Handles both author and publish modes with CardOverview child components.
 *
 * Key Features:
 * - AEM authoring integration via EditableComponent
 * - Dual mode rendering (author/publish)
 * - Responsive configurations
 * - Performance optimizations
 *
 * @module organisms/CobrandCarousel
 */

import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { AEMReactCompMap, CardVertical, CardVerticalVariations } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { FC, useEffect, Suspense, useRef, useState, useMemo, ReactNode } from 'react';
import { CobrandVanilaCarousel as CardCarouselContainerMolecule } from '../../index';
import { CardCarouselProps, CarouselContainerProps, CqItems, CqCardItem } from './CobrandCarousel.types';
import { StyledCobrandCarousel } from './CobrandCarousel.styles';
import { isMobile, setObserver } from '../../modules/utils';
import { RESOLVED_URL } from '../../modules/constants';
import { CardOverview } from '../CardOverview';
import { useStore } from '../../modules/store/cobrandStore';

export const CobrandCarouselConfig = {
  emptyLabel: 'CobrandCarousel',
  isEmpty: () => true,
  resourceType: `mi-aem-common-spa/components/content/cardcarouselcontainer`,
};

/**
 * CobrandCarouselComp - Main carousel component implementation
 *
 * @param props - CardCarouselProps
 */
export const CobrandCarouselComp: FC<CardCarouselProps> = (props: CardCarouselProps) => {
  const {
    headerText,
    subHeaderText,
    ctaLabel,
    ctaLink,
    eyebrow,
    ctaType,
    cardCount,
    openInaNewTab,
    totalNumberOfCards,
    styleclass,
    cqItems,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    variation,
    enableTabletBreakpoint = true,
    isCardComparisonFeatureEnabled,
    trackingProperties,
    isRtl,
    layout,
    cardTag,
    resolvedUrl,
  } = props;

  // ========================================================================
  // State & Refs
  // ========================================================================

  const cobrandCarouselRef = useRef<HTMLDivElement>(null);
  const [isMobileVariant, setIsMobileVariant] = useState(false);
  const isCobrandPage = resolvedUrl?.includes(RESOLVED_URL.CREDITCARDURL);
  useEffect(() => {
    setIsMobileVariant(isMobile());
  }, []);

  // ========================================================================
  // Store Integration
  // ========================================================================

  const { setCobrandData } = useStore(store => store);

  // ========================================================================
  // Computed Values
  // ========================================================================

  /**
   * Cards per view from props
   */
  let noOfCards = props.noOfCards;
  let noOfCardsTablet = props.noOfCardsTablet;
  const isCombo = (): boolean => {
    return variation === 'combo';
  };
  if (isCombo()) {
    noOfCards = 2;
    noOfCardsTablet = 2;
  }
  /**
   * Get AEM component mapper
   */
  const mapper = useMemo(() => AEMReactCompMap(props?.allowedComponents), [props?.allowedComponents]);

  /**
   * Collect selected card IDs from totalNumberOfCards
   */
  const selectedCards: { cardname?: string; checked?: boolean }[] = useMemo(() => [], []);

  // ========================================================================
  // Effects
  // ========================================================================

  /**
   * Effect: Set up intersection observer
   */
  useEffect(() => {
    if (componentId && cobrandCarouselRef.current) {
      setObserver(componentId, cobrandCarouselRef);
    }
  }, [componentId]);

  /**
   * Effect: Update cobrand store with selected cards
   */
  useEffect(() => {
    const { cobrandData } = useStore.getState();
    setCobrandData({ ...cobrandData, allCarouselCards: selectedCards });
  }, [selectedCards, setCobrandData]);

  // ========================================================================
  // Render Functions - Author Mode
  // ========================================================================

  /**
   * Renders card wrapper for AEM authoring mode
   */
  const authorCardWrapper = (index: number, innerCompStyleClass: string | undefined): ReactNode => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${totalNumberOfCards[index]}`}
        customClassName={clsx(innerCompStyleClass)}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        }}
      />
    );
  };

  // ========================================================================
  // Render Functions - Publish Mode
  // ========================================================================

  /**
   * Memoized wrapper for all publish mode cards
   */
  const PublishCardsWrapper = useMemo(() => {
    const publishCardWrapper = (cardName: string, jsonData: CqItems): ReactNode | null => {
      // eslint-disable-next-line no-prototype-builtins
      if (!jsonData?.hasOwnProperty(cardName)) return null;

      const card = jsonData[cardName] as CqCardItem;
      const cardItems = card[':items'];

      if (!cardItems) return null;

      // Process card items
      for (const itemKey in cardItems) {
        if (!Object.prototype.hasOwnProperty.call(cardItems, itemKey) || !itemKey) continue;

        const item = cardItems[itemKey] as Record<string, unknown> & { ':type'?: string };

        // Process CardOverview
        if (itemKey?.includes('cardoverview') && noOfCards) {
          // Add custom class based on number of cards
          item['customClass'] =
            noOfCards === 2 ? 'card-overview-two-cards' : noOfCards === 3 ? 'card-overview-three-cards' : '';
        }
        if (itemKey.includes('cardvertical')) {
          return (
            <li key={itemKey}>
              <CardVertical ctaLink={''} {...item} cardVerticalVariations={CardVerticalVariations.Standard} />;
            </li>
          );
        }

        // Get item type and check mapper
        const itemType = item[':type']?.split('/').pop();
        if (!itemType || !Object.prototype.hasOwnProperty.call(mapper, itemType)) {
          continue;
        }

        const innerComp = mapper[itemType];

        // Render CardOverview component (AEMReactCompMap returns PascalCase from title)
        if (innerComp === 'CardOverview') {
          const cardOverviewProps = {
            ...item,
            isCardComparisonFeatureEnabled: isCardComparisonFeatureEnabled,
            layout: layout,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any; // Type assertion needed for dynamic AEM properties

          return (
            <li className="container px-0 d-flex" key={`${cardName}-${itemKey}`}>
              <Suspense fallback={<div></div>}>
                <CardOverview {...cardOverviewProps} />
              </Suspense>
            </li>
          );
        }
      }

      return null;
    };

    return totalNumberOfCards?.map((cardName: string) => publishCardWrapper(cardName, cqItems));
  }, [totalNumberOfCards, cqItems, noOfCards, mapper, isCardComparisonFeatureEnabled, layout]);

  // ========================================================================
  // Main Render
  // ========================================================================

  return (
    <StyledCobrandCarousel
      data-testid="cobrand-CobrandCarousel"
      data-component-name="o-cobrand-CobrandCarousel"
      ref={cobrandCarouselRef}
    >
      <div>
        <CardCarouselContainerMolecule
          componentId={componentId}
          subHeaderText={subHeaderText}
          styleclass={styleclass}
          variation={variation}
          eyebrow={eyebrow}
          cardCount={cardCount}
          trackingProperties={trackingProperties}
          ctaLabel={ctaLabel}
          headerText={headerText}
          ctaLink={ctaLink}
          openInaNewTab={openInaNewTab}
          ctaType={ctaType}
          isCombo={isCombo}
          noOfCards={noOfCards}
          noOfCardsTablet={noOfCardsTablet}
          enableTabletBreakpoint={enableTabletBreakpoint}
          isCobrandVariant={isCobrandPage && isMobileVariant}
          isDirectionRightToLeft={isRtl}
          layout={layout}
          cardTag={cardTag}
        >
          {isAuthorMode &&
            Array.from({ length: totalNumberOfCards?.length }, (_, i) => authorCardWrapper(i, styleclass))}
          {!isAuthorMode && PublishCardsWrapper}
        </CardCarouselContainerMolecule>
      </div>
    </StyledCobrandCarousel>
  );
};

CobrandCarouselComp.displayName = 'CobrandCarouselComp';

/**
 * CarouselEditableComponent - AEM editable wrapper
 *
 * Wraps CobrandCarouselComp with AEM EditableComponent for authoring
 *
 * @param props - Component props with model
 */
export const CarouselEditableComponent: FC<CarouselContainerProps> = (props: CarouselContainerProps) => {
  const { model, ...restProps } = props;
  return (
    <EditableComponent config={CobrandCarouselConfig} {...restProps}>
      <CobrandCarouselComp {...model} {...restProps} componentName={props?.model?.cqType?.split('/').pop()} />
    </EditableComponent>
  );
};

/**
 * CobrandCarousel - Top-level export component
 *
 * Applies styling classes and renders the editable carousel
 * Handles fullbleed, inverse, and alternate variations
 *
 * @param props - CarouselContainerProps with model
 */
export const CobrandCarousel: FC<CarouselContainerProps> = (props: CarouselContainerProps) => {
  const { model } = props;

  const isInverse = model.styleclass?.includes('inverse') ?? false;
  const isFullbleed = model.styleclass?.includes('fullbleed') ?? false;
  const isAlternate = model.styleclass?.includes('alternate') ?? false;

  return (
    <div
      className={clsx(isInverse && 'inverse', isFullbleed && 'm-container-fullbleed', isAlternate && 'alternate')}
      data-testid="card-carousel"
    >
      <div className={clsx('container', { 'p-0': !isFullbleed })}>
        <CarouselEditableComponent {...props} />
      </div>
    </div>
  );
};

CobrandCarousel.displayName = 'CobrandCarousel';
